import { useCallback, useRef, useState } from 'react';
import { useStoreWithEqualityFn as useStore } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { deriveSecretFromCryptoKey, secretToHashlock, bytesToHex, } from '@train-protocol/sdk';
import { deriveKeyWithPasskey, registerPasskey as sdkRegisterPasskey, deriveKeyFromWallet, checkPrfSupport, } from '@train-protocol/auth';
import { chainNamespace as brandChainNamespace } from '../internal/branded';
import { useWalletContextOptional } from '../wallet/WalletContext';
import { createSecretDerivationStore, } from '../internal/secretDerivationStore';
export function useSecretDerivation(options) {
    const shouldPersist = options?.persist === true;
    // Create store once (stable across renders)
    const storeRef = useRef(null);
    if (!storeRef.current) {
        storeRef.current = createSecretDerivationStore({
            persist: shouldPersist,
        });
    }
    const store = storeRef.current;
    // Single store subscription with shallow equality (consolidates 6 separate subscriptions)
    const { method, derivedKey, derivationStatus, derivationMessage, prfSupport, hydrated, credentialVersion } = useStore(store, (s) => ({
        method: s.method,
        derivedKey: s.derivedKey,
        derivationStatus: s.derivationStatus,
        derivationMessage: s.derivationMessage,
        prfSupport: s.prfSupport,
        hydrated: s.hydrated,
        credentialVersion: s.credentialVersion,
    }), shallow);
    // Wallet context (optional — works outside TrainProvider too)
    const walletCtx = useWalletContextOptional();
    const passkeyStorage = options?.passkeyStorage;
    const isLoggedIn = !!method && !!derivedKey;
    const [error, setError] = useState(null);
    const loginWithPasskey = useCallback(async (options) => {
        setError(null);
        store.getState().setDerivationStatus('signing');
        store.getState().setDerivationMessage('Confirm with passkey');
        try {
            let key;
            let credentialId;
            if (options?.forceCreate) {
                const result = await sdkRegisterPasskey(true, options.label, passkeyStorage);
                if (result.key) {
                    key = result.key;
                    credentialId = result.credentialId;
                }
                else {
                    ;
                    ({ key, credentialId } = await deriveKeyWithPasskey({ createIfMissing: false }, passkeyStorage));
                }
            }
            else {
                ;
                ({ key, credentialId } = await deriveKeyWithPasskey({ createIfMissing: !options?.crossDevice }, passkeyStorage));
            }
            await passkeyStorage?.storeCredentialId(credentialId);
            store.getState().setLogin('passkey', key);
            store.getState().bumpCredentialVersion();
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
        finally {
            store.getState().setDerivationStatus('idle');
            store.getState().setDerivationMessage('');
        }
    }, [store, passkeyStorage]);
    const authInstance = options?.auth;
    const loginWithWallet = useCallback(async (chainNs, config) => {
        setError(null);
        const ns = brandChainNamespace(chainNs);
        const resolvedConfig = config ?? (await walletCtx?.getLoginConfig(ns));
        if (!resolvedConfig) {
            const error = new Error(`No login config available for "${chainNs}". ` +
                `Either pass config explicitly or ensure the wallet adapter implements getLoginConfig() and the wallet is connected.`);
            setError(error);
            throw error;
        }
        store.getState().setDerivationStatus('signing');
        store.getState().setDerivationMessage('Please sign in wallet');
        try {
            // Use auth instance from context if provided, otherwise fall back to free function
            const key = authInstance
                ? await authInstance.deriveKeyFromWallet(chainNs, resolvedConfig)
                : await deriveKeyFromWallet(chainNs, resolvedConfig);
            store.getState().setLogin('wallet_sign', key);
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
        finally {
            store.getState().setDerivationStatus('idle');
            store.getState().setDerivationMessage('');
        }
    }, [store, walletCtx, authInstance]);
    const logout = useCallback(() => {
        store.getState().logout();
    }, [store]);
    const deriveSecret = useCallback(async (nonce) => {
        const currentKey = store.getState().derivedKey;
        if (!currentKey)
            return null;
        const timestamp = nonce ?? Date.now();
        const secretBytes = await deriveSecretFromCryptoKey(currentKey, timestamp);
        const secret = bytesToHex(Array.from(secretBytes));
        const hashlock = secretToHashlock(secret);
        return { secret, nonce: timestamp, hashlock };
    }, [store]);
    const registerPasskey = useCallback(async (displayName) => {
        setError(null);
        store.getState().setDerivationStatus('signing');
        store.getState().setDerivationMessage('Register passkey');
        try {
            const { credentialId, key } = await sdkRegisterPasskey(true, displayName, passkeyStorage);
            if (key) {
                store.getState().setLogin('passkey', key);
            }
            await passkeyStorage?.storeCredentialId(credentialId);
            store.getState().bumpCredentialVersion();
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
        finally {
            store.getState().setDerivationStatus('idle');
            store.getState().setDerivationMessage('');
        }
    }, [store, passkeyStorage]);
    // Read from in-memory cache (sync) — IndexedDBPasskeyStorage returns sync from memory
    const passkeyCredentials = (passkeyStorage?.getAllCredentialIds() ?? []);
    const activePasskeyCredentialId = (passkeyStorage?.getActiveCredentialId() ?? null);
    // Subscribe to credentialVersion so React re-renders when credentials change.
    // The value itself is unused — reading it is enough to create the dependency.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    credentialVersion;
    const removePasskeyCredential = useCallback((id) => {
        passkeyStorage?.removeCredentialId?.(id);
        store.getState().bumpCredentialVersion();
    }, [store, passkeyStorage]);
    const checkPasskeySupport = useCallback(async () => {
        const result = await checkPrfSupport();
        store.getState().setPrfSupport(result);
        return result;
    }, [store]);
    // isReady: PRF support detected AND store hydrated (hydrated is true by default when persist is off)
    const isReady = prfSupport !== null && hydrated;
    return {
        isReady,
        method,
        isLoggedIn,
        derivedKey,
        derivationStatus,
        derivationMessage,
        error,
        loginWithPasskey,
        loginWithWallet,
        logout,
        deriveSecret,
        registerPasskey,
        passkeyCredentials,
        activePasskeyCredentialId,
        removePasskeyCredential,
        prfSupport,
        checkPasskeySupport,
        _store: store,
    };
}
